import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import FormField from '../../FormField/FormField';
import { useDebounce } from '../../../hooks/useDebounce';
import { useValidateCommercialNumberMutation } from '../../../store/services/addSellerService';

interface Props {
  dataTestId?: string;
  skipInitialValidation?: boolean;
}

export default function DebouncedCommercialNumber({
  dataTestId,
  skipInitialValidation = false,
}: Props) {
  const { t } = useTranslation();
  const { watch, setError, clearErrors } = useFormContext();

  const value = watch('commercialRegister');
  const debouncedValue = useDebounce(value, 500);
  const lastCheckedRef = useRef<string | null>(null);

  const [validateCommercialNumber, { isLoading }] =
    useValidateCommercialNumberMutation();

  const requiredError = t('components.validations.requiredField');

  const commercialSchema = z
    .string({ message: requiredError })
    .min(1, { message: requiredError })
    .regex(/^[0-9]+$/, { message: t('components.validations.acceptNumbersOnly') })
    .min(4, { message: t('components.validations.commercialRegisterLength') })
    .max(15, { message: t('components.validations.commercialRegisterLength') });

  useEffect(() => {
    if (skipInitialValidation) return;
    if (!debouncedValue) return;
    if (lastCheckedRef.current === debouncedValue) return;
    lastCheckedRef.current = debouncedValue;

    const { success } = commercialSchema.safeParse(debouncedValue);
    if (!success) return;

    let cancelled = false;

    (async () => {
      try {
        await validateCommercialNumber({
          commercialRegister: debouncedValue,
        }).unwrap();

        if (cancelled) return;
        clearErrors('commercialRegister');
      } catch (err: unknown) {
        if (cancelled) return;
        const error = err as { data?: { message?: string } };
        setError('commercialRegister', {
          type: 'validate',
          message: error?.data?.message || requiredError,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, skipInitialValidation]);

  return (
    <FormField
      id="commercialRegister"
      name="commercialRegister"
      type="text"
      label={t('sellers.addSeller.commercialRegister')}
      placeholder={t('sellers.addSeller.commercialRegisterPlaceholder')}
      disabled={isLoading}
      data-testid={dataTestId || 'add-seller-commercial-number'}
    />
  );
}
